<template>
  <div>
    <ContentHeader />

    <!-- Loading -->
    <div v-if="loading" class="loading">
      <div class="loader"></div>
      <img src="@/assets/img/logo.jpg" alt="logo" width="496" height="99" class="loaderLogo" />
    </div>

    <!-- Banner -->
    <div class="contactBanner" data-aos="fade-out">
      <span>text</span>
    </div>

    <!-- Contact Section -->
    <section class="contact-section py-5 bg-light" data-aos="fade-up">
      <div class="container">
        <!-- Heading -->
        <div class="text-center mb-5">
          <h1 class="leadTitle">Contact Us</h1>
          <p class="text-muted">
            Get in touch with Elemax-Tech Engineering Company for reliable elevator and lift solutions.
          </p>
        </div>

        <div class="row g-5">
          <!-- Contact Info -->
          <div class="col-md-5">
            <h4 class="fw-bold mb-3">Elemax-Tech Engineering Co., Ltd.</h4>
            <p>
              We are a trusted partner and installer of <strong>OTSTEC Elevators</strong>,
              providing high-quality lift systems for residential, commercial, and industrial projects.
            </p>

            <ul class="list-unstyled mt-4">
              <li class="mb-3">
                <i class="bi bi-geo-alt-fill text-primary me-2"></i>
                {{ contacts?.address }}
              </li>
              <li class="mb-3" v-for="(phone, index) in normalizedPhones" :key="index">
                <i class="bi bi-telephone-fill text-primary me-2"></i>
                {{ phone }}
              </li>
              <li class="mb-3">
                <i class="bi bi-envelope-fill text-primary me-2"></i>
                elemaxtechengineering.ete@gmail.com
              </li>
              <li>
                <i class="bi bi-globe text-primary me-2"></i>
                <a
                  href="https://www.otstec-elevator.com"
                  target="_blank"
                  class="text-decoration-none text-primary"
                >
                  Visit OTSTEC Elevator
                </a>
              </li>
            </ul>
          </div>

          <!-- Contact Form -->
          <div class="col-md-7">
            <div class="card shadow-sm border-0">
              <div class="card-body p-4">
                <h5 class="fw-bold mb-3">Send Us a Message</h5>

                <form @submit.prevent="sendMessage">
                  <div class="mb-3">
                    <label class="form-label">Full Name</label>
                    <input
                      type="text"
                      v-model="form.name"
                      class="form-control"
                      placeholder="Your name"
                      required
                    />
                  </div>

                  <div class="mb-3">
                    <label class="form-label">Email</label>
                    <input
                      type="email"
                      v-model="form.email"
                      class="form-control"
                      placeholder="Your email"
                      required
                    />
                  </div>

                  <div class="mb-3">
                    <label class="form-label">Message</label>
                    <textarea
                      v-model="form.message"
                      class="form-control"
                      rows="5"
                      placeholder="Your message..."
                      required
                    ></textarea>
                  </div>

                  <input type="hidden" v-model="form.recaptcha_token" />

                  <button type="submit" class="btn btn-primary w-100" :disabled="loading">
                    {{ loading ? 'Sending...' : 'Send Message' }}
                  </button>
<p class="recaptcha-notice">
  This site is protected by reCAPTCHA and the Google
  <a href="https://policies.google.com/privacy" target="_blank">Privacy Policy</a>
  and
  <a href="https://policies.google.com/terms" target="_blank">Terms of Service</a>
  apply.
</p>


                  <p v-if="success" class="text-success mt-3">{{ success }}</p>
                  <p v-if="error" class="text-danger mt-3">{{ error }}</p>
                </form>
              </div>
            </div>
          </div>
        </div>

        <!-- Google Map -->
        <div class="mt-5">
          <iframe
            :src="contacts?.map_embed"
            width="100%"
            height="400"
            style="border: 0"
            allowfullscreen
            loading="lazy"
          ></iframe>
        </div>
      </div>
    </section>

    <ContentFooter />
  </div>
</template>

<script>
/* global grecaptcha */
import ContentHeader from '@/components/ContentHeader.vue'
import ContentFooter from '@/components/ContentFooter.vue'
import axios from 'axios'

export default {
  name: 'ContactPage',
  components: { ContentHeader, ContentFooter },
  data() {
    return {
      contacts: null,
      form: { name: '', email: '', message: '', recaptcha_token: '' },
      loading: true,
      success: '',
      error: '',
      apiBase: process.env.VUE_APP_API_BASE,
      apiToken: process.env.VUE_APP_API_TOKEN,
      recaptchaSiteKey: process.env.VUE_APP_RECAPTCHA_V3_SITEKEY,
    }
  },
  methods: {
    async sendMessage() {
      this.success = ''
      this.error = ''

      // Basic validation
      if (!this.form.name.trim() || !this.form.email.trim() || !this.form.message.trim()) {
        this.error = 'All fields are required.'
        return
      }
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailPattern.test(this.form.email)) {
        this.error = 'Invalid email address.'
        return
      }

      try {
        this.loading = true

        // ✅ Execute Google reCAPTCHA v3
        const recaptchaToken = await grecaptcha.execute(this.recaptchaSiteKey, { action: 'submit' })
        this.form.recaptcha_token = recaptchaToken

        const payload = {
          name: this.form.name,
          email: this.form.email,
          message: this.form.message,
          recaptcha_token: recaptchaToken,
        }

        // ✅ Send to backend
        const response = await axios.post(`${this.apiBase}/api/contact/send`, payload, {
          headers: { Authorization: `Bearer ${this.apiToken}` },
        })

        // ✅ Success
        this.success = response.data.message || 'Message sent successfully!'
        this.form = { name: '', email: '', message: '', recaptcha_token: '' }
      } catch (err) {
        console.error('❌ Error:', err)

        // ✅ Handle reCAPTCHA or validation error
        if (err.response?.data) {
          this.error = err.response.data.message || 'reCAPTCHA verification failed.'
          if (err.response.data.score !== undefined) {
            this.error += ` (Score: ${err.response.data.score})`
          }
        } else {
          this.error = 'Something went wrong. Please try again.'
        }
      } finally {
        this.loading = false
      }
    },
  },
  computed: {
    normalizedPhones() {
      if (!this.contacts || !this.contacts.phone) return []
      if (Array.isArray(this.contacts.phone)) return this.contacts.phone
      try {
        const parsed = JSON.parse(this.contacts.phone)
        return Array.isArray(parsed) ? parsed : [parsed]
      } catch {
        return [this.contacts.phone]
      }
    },
  },
  async mounted() {
    try {
      const response = await axios.get(`${this.apiBase}/api/contacts`, {
        headers: { Authorization: `Bearer ${this.apiToken}` },
      })
      this.contacts = response.data
    } catch (error) {
      console.error(error)
    } finally {
      this.loading = false

      // ✅ Initialize reCAPTCHA
      if (window.grecaptcha && this.recaptchaSiteKey) {
        grecaptcha.ready(() => {
          console.log('✅ reCAPTCHA is ready')
        })
      } else {
        console.warn('⚠️ reCAPTCHA not loaded')
      }
    }
  },
}
</script>